import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';

// Heart toggle for saving a product. Works two ways:
//   variant="overlay" — small circular button floated over a shop-grid image
//   variant="inline"  — full pill button for the product detail page
// Guests who tap it get sent to /login (wishlist needs an account).
export default function WishlistButton({ product, variant = 'overlay' }) {
  const { user } = useAuth();
  const { has, add, remove } = useWishlist();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const saved = has(product.id);

  const toggle = async (e) => {
    // These often live inside a <Link> card — don't navigate to the product.
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate('/login?next=/account/wishlist'); return; }
    if (busy) return;
    setBusy(true);
    if (saved) await remove(product.id);
    else await add(product);
    setBusy(false);
  };

  if (variant === 'inline') {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className="sv-btn-outline"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 22px' }}
        aria-pressed={saved}
      >
        <Heart size={16} fill={saved ? 'var(--accent)' : 'none'} color={saved ? 'var(--accent)' : 'currentColor'} />
        {saved ? 'In your wishlist' : 'Add to wishlist'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-label={saved ? 'Remove from wishlist' : 'Add to wishlist'}
      aria-pressed={saved}
      style={{
        position: 'absolute', top: 10, right: 10, zIndex: 2,
        width: 34, height: 34, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'color-mix(in srgb, var(--bg) 80%, transparent)',
        border: '1px solid var(--border)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        cursor: 'pointer', color: saved ? 'var(--accent)' : 'var(--text)',
      }}
    >
      <Heart size={17} fill={saved ? 'var(--accent)' : 'none'} color={saved ? 'var(--accent)' : 'currentColor'} />
    </button>
  );
}
